import React, { useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import StampGrid from './StampGrid';

const DAYS_EN = [
  {
    num: 1, theme: 'The Big Idea', color: '#FF6B35', emoji: '💡',
    morning: 'Welcome & Icebreaker · Mini Lesson: What Is an Entrepreneur?',
    afternoon: 'From Problem to Business Idea · My First Business Idea Card',
    vocab: ['Entrepreneur', 'Problem', 'Idea', 'Customer', 'Business'],
    activity: 'Problem Spotter Sheet + Business Idea Card',
  },
  {
    num: 2, theme: 'Know Your Customer', color: '#06D6A0', emoji: '👥',
    morning: 'Yes, And... Game · Who Is a Customer? · Customer Profile Card',
    afternoon: 'Human Bingo · Customer Interview Roleplay · Update Business Idea',
    vocab: ['Customer', 'Need', 'Empathy', 'Feedback', 'Target Customer'],
    activity: 'Customer Profile Card + Updated Idea Card (Version 2)',
  },
  {
    num: 3, theme: 'Build It & Brand It', color: '#118AB2', emoji: '🏗️',
    morning: 'Spaghetti Tower Challenge · What Makes a Great Product? · Product Design',
    afternoon: '60-Second Logo Sprint · Business Poster · Prototype Build',
    vocab: ['Prototype', 'Brand', 'Logo', 'Slogan', 'Design'],
    activity: 'Product Design Sheet + Business Poster + Prototype',
  },
  {
    num: 4, theme: 'Money & Market', color: '#A855F7', emoji: '💰',
    morning: 'The Price Is Right! · Cost, Price & Profit · Price Your Product',
    afternoon: 'Money Math Challenge · Mini Market Simulation · Pitch Practice',
    vocab: ['Cost', 'Price', 'Profit', 'Value', 'Revenue'],
    activity: 'Pricing Worksheet + Pitch Planner',
  },
  {
    num: 5, theme: 'Pitch Day!', color: '#EC4899', emoji: '🎤',
    morning: 'Superhero Warm-Up · Final Pitch Preparation · Setup',
    afternoon: 'PITCH DAY — All Presentations · Celebration & Recognition',
    vocab: ['Pitch', 'Confidence', 'Feedback'],
    activity: 'Final Pitch + Recognition Ceremony',
  },
];

const DAYS_JA = [
  {
    num: 1, theme: 'ビッグアイデア', color: '#FF6B35', emoji: '💡',
    morning: 'ウォームアップ「2つの本当と1つの夢」・ミニ授業：アントレプレナーとは何か？',
    afternoon: '問題からビジネスアイデアへ・私のビジネスアイデアカード作成',
    vocab: ['アントレプレナー', '問題', 'アイデア', 'お客様', 'ビジネス'],
    activity: '問題スポッターシート＋ビジネスアイデアカード',
  },
  {
    num: 2, theme: 'お客様を知ろう', color: '#06D6A0', emoji: '👥',
    morning: '「そうだね、そして...」ゲーム・お客様とは誰か？・お客様プロフィールカード',
    afternoon: '人間ビンゴ・お客様インタビューロールプレイ・アイデアカードをアップデート',
    vocab: ['お客様', 'ニーズ', '共感', 'フィードバック', 'ターゲット顧客'],
    activity: 'お客様プロフィールカード＋アップデートしたアイデアカード（バージョン2）',
  },
  {
    num: 3, theme: '作って・ブランドにしよう', color: '#118AB2', emoji: '🏗️',
    morning: 'スパゲッティタワーチャレンジ・良い商品とは？・商品デザインシート',
    afternoon: '60秒ロゴスプリント・ビジネスポスター作成・プロトタイプ制作',
    vocab: ['プロトタイプ', 'ブランド', 'ロゴ', 'スローガン', 'デザイン'],
    activity: '商品デザインシート＋ビジネスポスター＋プロトタイプ',
  },
  {
    num: 4, theme: 'お金とマーケット', color: '#A855F7', emoji: '💰',
    morning: '「お値段はいくら？」ゲーム・コスト・価格・利益・価格設定ワーク',
    afternoon: 'お金計算早押しクイズ・ミニマーケットシミュレーション・発表練習',
    vocab: ['コスト', '価格', '利益', '価値', '収益'],
    activity: '価格設定ワークシート＋ピッチプランナー',
  },
  {
    num: 5, theme: 'ピッチ発表の日！', color: '#EC4899', emoji: '🎤',
    morning: 'スーパーヒーローウォームアップ・最終発表準備・会場設営',
    afternoon: 'ピッチ発表DAY・お祝いと表彰式',
    vocab: ['ピッチ', '自信', 'フィードバック'],
    activity: '最終ピッチ発表＋表彰式',
  },
];

export default function Booklet({ student, stamps, activities, programId }) {
  const { t, lang } = useApp();
  const ref = useRef();
  const DAYS = lang === 'ja' ? DAYS_JA : DAYS_EN;

  const getActivity = (day, key) =>
    activities?.find(a => a.program_id === programId && a.day_number === day && a.activity_key === key);

  const getStampsForDay = (day) =>
    stamps?.filter(s => s.program_id === programId && s.day_number === day) || [];

  const handlePrint = () => window.print();

  const displayName = lang === 'ja' ? (student.name_ja || student.name) : student.name;

  return (
    <div>
      {/* Print button */}
      <div className="d-flex justify-content-between align-items-center mb-3 no-print">
        <div style={{ fontSize: '0.82rem', color: 'var(--muted)', fontWeight: 600 }}>
          💡 {lang === 'ja'
            ? 'ブラウザの印刷ダイアログで「用紙サイズ: A4」「余白: デフォルト」を選んでください'
            : 'In the print dialog: Paper size A4 · Margins Default · Color'}
        </div>
        <button className="btn-summer" onClick={handlePrint} style={{ flexShrink: 0, padding: '0.5rem 1.5rem' }}>
          🖨️ {lang === 'ja' ? 'ブックレットを印刷' : 'Print Booklet'}
        </button>
      </div>

      <div ref={ref} className="booklet-print-wrapper">
        {/* ── Fixed footer — repeats on every printed page ── */}
        <div className="booklet-print-footer">
          <span className="footer-brand">🌺 Ryukyu GLOCAL Kids Summer School 2026</span>
          <span>{displayName}</span>
          <span className="footer-page">{lang === 'ja' ? 'ページ ' : 'Page '}</span>
        </div>
        {/* Booklet Cover */}
        <div className="booklet-page booklet-cover mb-4" style={{ background: 'linear-gradient(160deg, #FF6B35 0%, #FF9A5C 40%, #FFD166 100%)', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="text-center" style={{ padding: '2rem' }}>
            {/* Circular logo */}
            <div style={{
              width: 140, height: 140, borderRadius: '50%',
              background: 'white',
              border: '4px solid rgba(255,255,255,0.7)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1.8rem',
              overflow: 'hidden',
            }}>
              <img src="/logo.png" alt="Ryukyu GLOCAL Kids" style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
            </div>

            <div style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.25em', opacity: 0.85, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
              Ryukyu GLOCAL
            </div>

            <h2 style={{ fontWeight: 900, fontSize: '2.2rem', marginBottom: '0.4rem', lineHeight: 1.2 }}>
              {lang === 'ja' ? 'キッズ・アントレプレナーシップ' : 'Kids Entrepreneurship'}
            </h2>
            <div style={{ fontSize: '1.1rem', opacity: 0.9, fontWeight: 700, marginBottom: '2rem' }}>
              {lang === 'ja' ? 'サマースクール 2026 · 沖縄' : 'Summer School 2026 · Okinawa'}
            </div>

            <div style={{ fontSize: '2.2rem', letterSpacing: '0.6rem', marginBottom: '2.5rem' }}>🌊🏄🌴💡</div>

            {/* Student name card */}
            <div style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(6px)', borderRadius: 20, padding: '1.2rem 2.5rem', display: 'inline-block', minWidth: 240, border: '1px solid rgba(255,255,255,0.35)', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}>
              <div style={{ fontSize: '0.72rem', opacity: 0.8, fontWeight: 700, marginBottom: '0.4rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                {lang === 'ja' ? '生徒名' : 'Student Name'}
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 900 }}>{displayName}</div>
            </div>

            {/* Programme label */}
            <div style={{ marginTop: '2rem', opacity: 0.75, fontSize: '0.8rem', fontWeight: 600 }}>
              {lang === 'ja' ? 'アントレプレナーシップ・ブックレット' : 'Entrepreneurship Activity Booklet'}
            </div>
          </div>
        </div>

        {/* Stamp overview */}
        <div className="booklet-page booklet-stamps">
          <div className="booklet-day-header" style={{ justifyContent: 'center', textAlign: 'center' }}>
            <span style={{ fontSize: '1.5rem' }}>⭐</span>
            <div>
              <h5 style={{ fontWeight: 900, margin: 0 }}>{t.stamps}</h5>
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{t.weekProgress}</div>
            </div>
          </div>
          <StampGrid stamps={stamps || []} programId={programId} programSlug="kids-entrepreneurship" />
        </div>

        {/* Day pages */}
        {DAYS.map((day, i) => {
          const dayStamps = getStampsForDay(day.num);
          const ideaActivity = getActivity(day.num, 'business_idea');
          const hasContent = ideaActivity?.content;

          return (
            <div key={day.num} className="booklet-page" style={{ borderTopColor: day.color }}>
              <div className="booklet-day-header">
                <div>
                  <div className="day-badge" style={{ background: `linear-gradient(135deg, ${day.color}, ${day.color}88)` }}>
                    {lang === 'ja' ? `${day.num}日目` : `Day ${day.num}`} {day.emoji}
                  </div>
                </div>
                <div>
                  <h5 style={{ fontWeight: 900, margin: 0, color: day.color }}>{day.theme}</h5>
                </div>
                {/* Mini stamps for this day */}
                <div className="ms-auto d-flex gap-1">
                  {['morning', 'afternoon'].map(s => {
                    const earned = dayStamps.find(st => st.session === s);
                    return (
                      <div key={s} style={{
                        width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1.1rem',
                        background: earned ? `linear-gradient(135deg, ${day.color}, #FFD166)` : '#F3F4F6',
                        boxShadow: earned ? `0 4px 12px ${day.color}44` : 'none',
                      }}>
                        {earned ? '⭐' : ''}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Key vocabulary */}
              <div className="activity-section mb-3" style={{ borderLeftColor: day.color }}>
                <h6 style={{ color: day.color }}>{lang === 'ja' ? '重要ワード' : 'Key Vocabulary'}</h6>
                <div className="d-flex flex-wrap gap-2">
                  {day.vocab.map(v => (
                    <span key={v} style={{ background: `${day.color}18`, color: day.color, borderRadius: 8, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700 }}>
                      {v}
                    </span>
                  ))}
                </div>
              </div>

              {/* Sessions */}
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <div className="activity-section h-100" style={{ borderLeftColor: '#FFD166' }}>
                    <h6 style={{ color: '#F59E0B' }}>☀️ {t.morning}</h6>
                    <div style={{ fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.6 }}>{day.morning}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="activity-section h-100" style={{ borderLeftColor: day.color }}>
                    <h6 style={{ color: day.color }}>🌙 {t.afternoon}</h6>
                    <div style={{ fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.6 }}>{day.afternoon}</div>
                  </div>
                </div>
              </div>

              {/* Activity / Work space */}
              <div className="activity-section" style={{ borderLeftColor: '#E5E7EB', background: '#FAFAFA' }}>
                <h6 style={{ color: 'var(--muted)' }}>📝 {t.activity}</h6>
                {hasContent ? (
                  <div style={{ fontSize: '0.85rem', lineHeight: 1.7 }}>
                    {Object.entries(hasContent).map(([k, v]) => v && (
                      <div key={k} className="mb-1">
                        <span style={{ fontWeight: 700, color: day.color }}>{k.replace(/_/g, ' ')}: </span>
                        <span>{v}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8rem', color: '#D1D5DB', fontStyle: 'italic' }}>
                    {day.activity}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
